import { makeStyles } from '@material-ui/styles'
import React, { useEffect, useState } from 'react'
import { useCookies } from 'react-cookie'
import { useTranslation } from 'react-i18next'
import { PLATFORM_ID_COOKIE } from '../../constants/cookies'
import { URL } from '../../constants/url'
import { saveContactList, deleteContactList, addContactsToList, deleteContactFromList } from '../../containers/DataContainer'
import api from '../../utils/Requester'
import Dialog from '@material-ui/core/Dialog'
import DialogTitle from '@material-ui/core/DialogTitle'
import DialogContent from '@material-ui/core/DialogContent'
import DialogContentText from '@material-ui/core/DialogContentText'
import DialogActions from '@material-ui/core/DialogActions'
import Grid from '@material-ui/core/Grid'
import Paper from '@material-ui/core/Paper'
import Typography from '@material-ui/core/Typography'
import List from '@material-ui/core/List'
import ListItem from '@material-ui/core/ListItem'
import ListItemText from '@material-ui/core/ListItemText'
import IconButton from '@material-ui/core/IconButton'
import TextField from '@material-ui/core/TextField'
import Divider from '@material-ui/core/Divider'
import Button from '@material-ui/core/Button'
import EditIcon from '@material-ui/icons/Edit'
import DeleteIcon from '@material-ui/icons/Delete'
import { useTotalTitleBarContext } from '../../contexts/TotalTitleBarContext'
import CreateContact from '../../components/forms/PreventionPlanForms/CreateContact'
import AddContactForm from '../../components/forms/PreventionPlanForms/AddContactForm'

const useStyles = makeStyles((theme) => ({
    root: {
        padding: theme.spacing.unit * 3,
    },
    panel: {
        height: '100%',
        display: 'flex',
        flexDirection: 'column',
        border: '1px solid #ececec',
    },
    panelHeader: {
        padding: theme.spacing.unit * 2,
        borderBottom: '1px solid #ececec',
    },
    panelHeaderTop: {
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'space-between',
        gap: theme.spacing.unit,
    },
    panelContent: {
        flex: 1,
        overflowY: 'auto',
    },
    emptyState: {
        padding: theme.spacing.unit * 2,
        color: '#6f6f6f',
    },
    itemText: {
        marginRight: theme.spacing.unit,
    },
    selectableListItem: {
        cursor: 'pointer',
        transition: 'background-color 0.2s ease',
        '&:hover': {
            backgroundColor: '#f7f9fc',
        },
    },
    selectedListItem: {
        backgroundColor: '#dceeff !important',
        borderLeft: '5px solid #0068b3',
        boxShadow: 'inset 0 0 0 1px rgba(0, 104, 179, 0.25)',
        '& $itemText span': {
            fontWeight: 700,
            color: '#003b66',
        },
    },
    formContainer: {
        padding: theme.spacing.unit * 2,
        borderBottom: '1px solid #ececec',
    },
    actions: {
        display: 'flex',
        justifyContent: 'flex-end',
        gap: theme.spacing.unit,
        marginTop: theme.spacing.unit,
    },
    errorMessage: {
        marginTop: theme.spacing.unit,
    },
    contactSecondary: {
        fontSize: '0.85rem',
        color: '#646464',
        display: 'block',
    },
}))

function ContactLists() {
    const classes = useStyles()
    const { setTitleBar } = useTotalTitleBarContext()
    const { t } = useTranslation(['contacts'])
    const [cookies] = useCookies()
    const [contactLists, setContactLists] = useState([])
    const [contacts, setContacts] = useState([])
    const [selectedListId, setSelectedListId] = useState(null)
    const [isAddContactFormOpen, setIsAddContactFormOpen] = useState(false)
    const [isCreateContactOpen, setIsCreateContactOpen] = useState(false)
    const [editingContact, setEditingContact] = useState(null)
    const [showListForm, setShowListForm] = useState(false)
    const [editingListId, setEditingListId] = useState(null)
    const [listName, setListName] = useState('')
    const [listSaveError, setListSaveError] = useState('')
    const [pendingDeleteListId, setPendingDeleteListId] = useState(null)
    const [addContactsError, setAddContactsError] = useState('')
    const [pendingDeleteContact, setPendingDeleteContact] = useState(null)

    const selectedList = contactLists.find((list) => list.id === selectedListId) || null
    const filteredContacts = selectedListId
        ? contacts.filter((contact) => contact.listId === selectedListId)
        : []

    const sortLists = (lists) => [...lists].sort((a, b) => a.name.localeCompare(b.name, 'fr', { sensitivity: 'base' }))
    const sortContacts = (arr) =>
        [...arr].sort((a, b) =>
            `${a.lastName} ${a.firstName}`.localeCompare(`${b.lastName} ${b.firstName}`, 'fr', { sensitivity: 'base' }),
        )

    const fetchContactLists = async () => {
        const platformId = cookies[PLATFORM_ID_COOKIE]
        if (!platformId) return
        try {
            const res = await api.get(`${URL}platform/${platformId}/contact-lists`)
            if (res.problem || !Array.isArray(res.data)) {
                setContactLists([])
                setContacts([])
                return
            }
            const safeLists = res.data
            const mappedLists = safeLists.map((item) => ({
                id: String(item.lctId),
                name: item.lctNom,
                ptfId: item.ptfId,
                users: Array.isArray(item.users) ? item.users : [],
            }))

            const mappedContacts = mappedLists.flatMap((list) =>
                list.users.map((user) => {
                    const userId = user.utlId != null ? String(user.utlId) : Date.now().toString()
                    return {
                        id: `${list.id}-${userId}`,
                        sourceUserId: userId,
                        listId: list.id,
                        firstName: user.utlPrenom || '',
                        lastName: user.utlNom || '',
                        email: user.utlEmail || '',
                        entity: user.cppEntite || '',
                    }
                }),
            )

            setContactLists(sortLists(mappedLists))
            setContacts(sortContacts(mappedContacts))
        } catch (err) {
            setContactLists([])
            setContacts([])
        }
    }


    useEffect(() => {
        setTitleBar({
            type: 'titleBar',
            data: { icon: 'contacts', title: () => t('contact_lists_management') },
        })
    }, [])

    // Fetch contact lists from API on mount
    useEffect(() => {
        fetchContactLists()
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [cookies])

    const resetListForm = () => {
        setListName('')
        setEditingListId(null)
        setShowListForm(false)
        setListSaveError('')
    }

    const toLocalList = (item) => ({
        id: String(item.lctId != null ? item.lctId : item.LctId),
        name: item.lctNom != null ? item.lctNom : item.LctNom,
        ptfId: item.ptfId != null ? item.ptfId : item.PtfId,
        users: Array.isArray(item.users) ? item.users : [],
    })

    const handleSaveList = async () => {
        const trimmedName = listName.trim()
        if (!trimmedName) return

        setListSaveError('')

        const platformId = cookies[PLATFORM_ID_COOKIE]
        const hasPlatformId = platformId != null && platformId !== ''

        const payload = {
            LctId: editingListId ? Number(editingListId) : 0,
            LctNom: trimmedName,
            PtfId: hasPlatformId ? Number(platformId) : 0,
        }

        if (hasPlatformId) {
            try {
                const res = await saveContactList(platformId, payload)
                const savedList = res ? res.data || null : null

                const normalizedList = savedList
                    ? toLocalList(savedList)
                    : {
                          id: editingListId || String(Date.now()),
                          name: trimmedName,
                          ptfId: platformId,
                          users: [],
                      }

                if (editingListId) {
                    setContactLists((prev) =>
                        sortLists(prev.map((item) => (item.id === editingListId ? { ...item, ...normalizedList } : item))),
                    )
                    setSelectedListId(editingListId)
                } else {
                    await fetchContactLists()
                    setSelectedListId(normalizedList.id)
                }

                setListSaveError('')
                resetListForm()
                return
            } catch (error) {
                setListSaveError(t('error_saving_on_server'))
            }
        }
    }

    const handleEditList = (item) => {
        setEditingListId(item.id)
        setListName(item.name)
        setShowListForm(true)
        setListSaveError('')
        setSelectedListId(item.id) // Sélectionner la liste lors de l'édition
    }

    const handleEditContact = (item) => {
        setEditingContact(item)
        setIsCreateContactOpen(true)
    }

    const handleDeleteList = (listId) => {
        setPendingDeleteListId(listId)
    }

    const handleConfirmDeleteList = async () => {
        const listId = pendingDeleteListId
        setPendingDeleteListId(null)

        const platformId = cookies[PLATFORM_ID_COOKIE]
        try {
            await deleteContactList(platformId, listId)
        } catch (error) {
            // Si l'API échoue, on ne supprime pas localement
            return
        }

        setContactLists((prev) => prev.filter((item) => item.id !== listId))
        setContacts((prev) => prev.filter((item) => item.listId !== listId))

        if (selectedListId === listId) {
            setSelectedListId(null)
            setIsAddContactFormOpen(false)
            setIsCreateContactOpen(false)
            setEditingContact(null)
        }

        if (editingListId === listId) {
            resetListForm()
        }
    }

    const handleDeleteContact = (contact) => {
        setPendingDeleteContact(contact)
    }

    const handleConfirmDeleteContact = async () => {
        const contact = pendingDeleteContact
        setPendingDeleteContact(null)

        const platformId = cookies[PLATFORM_ID_COOKIE]
        try {
            await deleteContactFromList(platformId, contact.listId, contact.sourceUserId)
        } catch (error) {
            setAddContactsError(t('error_deleting_contact'))
            return
        }

        setContacts((prev) => prev.filter((item) => item.id !== contact.id))
        if (editingContact && editingContact.id === contact.id) {
            setEditingContact(null)
            setIsCreateContactOpen(false)
        }
    }

    const openAddContactForm = () => {
        if (!selectedListId) return
        setIsAddContactFormOpen(true)
    }

    const handleAddContactsFromDirectory = async (selectedContacts) => {
        if (!selectedListId || !Array.isArray(selectedContacts)) {
            return
        }

        const normalizedContacts = selectedContacts.map((item) => ({
            id: `${selectedListId}-${item.utlId != null ? String(item.utlId) : String(item.id)}`,
            sourceUserId: item.utlId != null ? String(item.utlId) : String(item.id),
            firstName: item.utlPrenom || item.firstName || '',
            lastName: item.utlNom || item.lastName || item.name || '',
            email: item.utlEmail || item.email || '',
            entity: item.cppEntite || item.entity || '',
        }))

        const platformId = cookies[PLATFORM_ID_COOKIE]
        const contactIds = normalizedContacts
            .map((c) => Number(c.sourceUserId))
            .filter((id) => !isNaN(id) && id > 0)

        try {
            await addContactsToList(platformId, selectedListId, contactIds)
        } catch (error) {
            // L'API a échoué : on retire les contacts ajoutés par AddContactForm (qui ferme lui-même le dialog)
            setAddContactsError("Erreur lors de l'ajout des contacts. Veuillez réessayer.")
            setContacts((prev) => {
                const addedIds = new Set(normalizedContacts.map((c) => c.id))
                return prev.filter((item) => !addedIds.has(item.id))
            })
            return
        }

        setAddContactsError('')
        setContacts((prev) => {
            const next = [...prev]
            normalizedContacts.forEach((contact) => {
                const existingIndex = next.findIndex((item) => item.id === contact.id)
                if (existingIndex >= 0) {
                    next[existingIndex] = { ...next[existingIndex], ...contact, listId: selectedListId }
                } else {
                    next.push({ ...contact, listId: selectedListId })
                }
            })
            return sortContacts(next)
        })
    }

    const handleCreateOrUpdateContact = (formData) => {
        const contactId = formData.contactId ? String(formData.contactId) : Date.now().toString()
        const targetListId = editingContact ? editingContact.listId : selectedListId

        if (!targetListId) {
            setIsCreateContactOpen(false)
            return
        }

        const normalizedContact = {
            id: contactId,
            listId: targetListId,
            firstName: formData.firstName,
            lastName: formData.lastName,
            email: formData.email,
            entity: formData.entity,
        }

        if (editingContact) {
            setContacts((prev) =>
                sortContacts(prev.map((item) =>
                    item.id === editingContact.id ? { ...item, ...normalizedContact } : item,
                )),
            )
        } else {
            setContacts((prev) => sortContacts([...prev, normalizedContact]))
        }

        setIsCreateContactOpen(false)
        setEditingContact(null)
    }

    const createContactData = {
        data: editingContact
            ? {
                  id: editingContact.id,
                  name: editingContact.lastName,
                  firstName: editingContact.firstName,
                  email: editingContact.email,
                  entity: editingContact.entity,
              }
            : null,
        onSubmitContact: editingContact ? handleCreateOrUpdateContact : undefined,
        pdpId: undefined,
        revisionNumber: undefined,
        setNeedRefresh: () => {
            // ContactLists does not use PDP refresh state.
        },
    }

    const addContactData = {
        pdpId: undefined,
        revisionNumber: undefined,
        onSubmitSelectedContacts: handleAddContactsFromDirectory,
    }

    return (
        <div className={classes.root}>
            <AddContactForm
                open={isAddContactFormOpen}
                close={() => setIsAddContactFormOpen(false)}
                data={addContactData}
            />
            <CreateContact
                open={isCreateContactOpen}
                close={() => {
                    setIsCreateContactOpen(false)
                    setEditingContact(null)
                }}
                data={createContactData}
            />
            <Grid container spacing={3}>
                <Grid item xs={12} md={6}>
                    <Paper className={classes.panel} elevation={1}>
                        <div className={classes.panelHeader}>
                            <div className={classes.panelHeaderTop}>
                                <Typography variant="h6">{t('contact_lists')}</Typography>
                                <Button
                                    variant="contained"
                                    color="primary"
                                    onClick={() => {
                                        setEditingListId(null)
                                        setListName('')
                                        setListSaveError('')
                                        setShowListForm(true)
                                    }}
                                >
                                    {t('add_list')}
                                </Button>
                            </div>
                        </div>

                        {showListForm && (
                            <div className={classes.formContainer}>
                                <TextField
                                    label={t('list_name')}
                                    variant="outlined"
                                    fullWidth
                                    value={listName}
                                    onChange={(event) => {
                                        setListName(event.target.value)
                                        if (listSaveError) {
                                            setListSaveError('')
                                        }
                                    }}
                                    inputProps={{ maxLength: 100 }}
                                    helperText={`${listName.length}/100`}
                                />
                                {listSaveError && (
                                    <Typography variant="body2" color="error" className={classes.errorMessage}>
                                        {listSaveError}
                                    </Typography>
                                )}
                                <div className={classes.actions}>
                                    <Button onClick={resetListForm}>Annuler</Button>
                                    <Button
                                        variant="contained"
                                        color="primary"
                                        onClick={handleSaveList}
                                        disabled={!listName.trim()}
                                    >
                                        {editingListId ? 'Enregistrer' : 'Ajouter'}
                                    </Button>
                                </div>
                            </div>
                        )}

                        <div className={classes.panelContent}>
                            {contactLists.length === 0 && (
                                <Typography className={classes.emptyState}>Aucune liste pour le moment.</Typography>
                            )}
                            <List disablePadding>
                                {contactLists.map((item) => (
                                    <React.Fragment key={item.id}>
                                        <ListItem
                                            button
                                            onClick={() => setSelectedListId(item.id)}
                                            className={
                                                selectedListId === item.id
                                                    ? classes.selectedListItem
                                                    : classes.selectableListItem
                                            }
                                        >
                                            <ListItemText className={classes.itemText} primary={item.name} />
                                            <IconButton
                                                aria-label="Modifier la liste"
                                                onClick={(event) => {
                                                    event.stopPropagation()
                                                    handleEditList(item)
                                                }}
                                            >
                                                <EditIcon />
                                            </IconButton>
                                            <IconButton
                                                aria-label="Supprimer la liste"
                                                onClick={(event) => {
                                                    event.stopPropagation()
                                                    handleDeleteList(item.id)
                                                }}
                                            >
                                                <DeleteIcon />
                                            </IconButton>
                                        </ListItem>
                                        <Divider />
                                    </React.Fragment>
                                ))}
                            </List>
                        </div>

                    </Paper>
                </Grid>

                <Grid item xs={12} md={6}>
                    <Paper className={classes.panel} elevation={1}>
                        <div className={classes.panelHeader}>
                            <div className={classes.panelHeaderTop}>
                                <Typography variant="h6">Contacts</Typography>
                                <Button
                                    variant="contained"
                                    color="primary"
                                    onClick={openAddContactForm}
                                    disabled={contactLists.length === 0 || !selectedListId}
                                >
                                    Ajouter un contact
                                </Button>
                            </div>
                            <Typography variant="body2" color="textSecondary">
                                {selectedList
                                    ? (
                                        <>
                                            {t('selected_list')}: <strong>{selectedList.name}</strong>
                                        </>
                                    )
                                    : t('select_list_to_view_contacts')}
                            </Typography>
                        </div>

                        <div className={classes.panelContent}>
                            {addContactsError && (
                                <Typography variant="body2" color="error" className={classes.errorMessage}>
                                    {addContactsError}
                                </Typography>
                            )}
                            {selectedListId && filteredContacts.length === 0 && (
                                <Typography className={classes.emptyState}>{t('no_contacts_yet')}</Typography>
                            )}
                            {!selectedListId && (
                                <Typography className={classes.emptyState}>
                                    {t('select_list_to_view_contacts')}
                                </Typography>
                            )}
                            <List disablePadding>
                                {filteredContacts.map((item) => (
                                    <React.Fragment key={item.id}>
                                        <ListItem>
                                            <ListItemText
                                                className={classes.itemText}
                                                primary={`${item.firstName} ${item.lastName}`}
                                                secondary={
                                                    <>
                                                        <span className={classes.contactSecondary}>{item.email}</span>
                                                        <span className={classes.contactSecondary}>{item.entity}</span>
                                                    </>
                                                }
                                            />
                                            <IconButton
                                                aria-label="Modifier le contact"
                                                onClick={() => handleEditContact(item)}
                                            >
                                                <EditIcon />
                                            </IconButton>
                                            <IconButton
                                                aria-label="Supprimer le contact"
                                                onClick={() => handleDeleteContact(item)}
                                            >
                                                <DeleteIcon />
                                            </IconButton>
                                        </ListItem>
                                        <Divider />
                                    </React.Fragment>
                                ))}
                            </List>
                        </div>

                    </Paper>
                </Grid>
            </Grid>

            <Dialog open={pendingDeleteListId !== null} onClose={() => setPendingDeleteListId(null)}>
                <DialogTitle>Supprimer la liste</DialogTitle>
                <DialogContent>
                    <DialogContentText>
                        Êtes-vous sûr de vouloir supprimer cette liste ? Cette action est irréversible.
                    </DialogContentText>
                </DialogContent>
                <DialogActions>
                    <Button onClick={() => setPendingDeleteListId(null)}>Annuler</Button>
                    <Button onClick={handleConfirmDeleteList} color="secondary" variant="contained">
                        Supprimer
                    </Button>
                </DialogActions>
            </Dialog>

            <Dialog open={pendingDeleteContact !== null} onClose={() => setPendingDeleteContact(null)}>
                <DialogTitle>{t('delete_contact')}</DialogTitle>
                <DialogContent>
                    <DialogContentText>
                        {t('confirm_delete_contact')}
                    </DialogContentText>
                </DialogContent>
                <DialogActions>
                    <Button onClick={() => setPendingDeleteContact(null)}>{t('cancel')}</Button>
                    <Button onClick={handleConfirmDeleteContact} color="secondary" variant="contained">
                        {t('delete')}
                    </Button>
                </DialogActions>
            </Dialog>
        </div>
    )
}

export default ContactLists
